import { useState } from "react";
import toast from "react-hot-toast";
import {
  ButtonBase,
  CircularProgress,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";

import { getStockLocation } from "../Api/index";
import {
  canSwitchBranch,
  getActingBranch,
  stopActingAsBranch,
  switchToBranch,
} from "../Utils/branchSwitch";

// Lets a Stock Administrator work as any location. Stays amber while switched,
// on every page, so nobody forgets whose stock they are looking at.
export default function BranchSwitcher() {
  const acting = getActingBranch();
  const [anchor, setAnchor] = useState(null);
  const [locations, setLocations] = useState(null);

  if (!canSwitchBranch()) return null;

  const open = async (event) => {
    setAnchor(event.currentTarget);
    if (locations) return;
    try {
      const { data } = await getStockLocation();
      setLocations(
        [...data].sort((a, b) => a.location.localeCompare(b.location)),
      );
    } catch (error) {
      setAnchor(null);
      toast.error(
        error?.response?.data?.message || "The locations could not be loaded",
      );
    }
  };

  return (
    <>
      <ButtonBase
        onClick={open}
        aria-label="Switch branch"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: 0.5,
          px: 1.25,
          py: 0.75,
          maxWidth: { xs: 170, sm: 240 },
          borderRadius: "8px",
          border: "1px solid",
          borderColor: acting ? "#F59E0B" : "rgba(255,255,255,0.3)",
          backgroundColor: acting ? "#FEF3C7" : "transparent",
          color: acting ? "#92400E" : "#fff",
          fontSize: 12.5,
          fontWeight: 600,
          "&:hover": {
            backgroundColor: acting ? "#FDE68A" : "rgba(255,255,255,0.08)",
          },
        }}
      >
        <SwapHorizRoundedIcon sx={{ fontSize: 18 }} />
        <Typography
          component="span"
          noWrap
          sx={{
            display: { xs: acting ? "block" : "none", md: "block" },
            minWidth: 0,
            fontSize: "inherit",
            fontWeight: "inherit",
            textTransform: "capitalize",
          }}
        >
          {acting ? `Working as ${acting}` : "Switch branch"}
        </Typography>
        <ExpandMore sx={{ fontSize: 18 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 230, maxHeight: 420 } }}
      >
        {acting && (
          <MenuItem onClick={stopActingAsBranch} sx={{ fontSize: 13, fontWeight: 600 }}>
            <ListItemIcon sx={{ minWidth: 30 }}>
              <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            Back to Administrator
          </MenuItem>
        )}
        {acting && <Divider />}

        {!locations && (
          <MenuItem disabled sx={{ fontSize: 13 }}>
            <ListItemIcon sx={{ minWidth: 30 }}>
              <CircularProgress size={16} />
            </ListItemIcon>
            Loading locations...
          </MenuItem>
        )}
        {locations?.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 13 }}>
            No locations have been set up
          </MenuItem>
        )}
        {locations?.map(({ _id, location }) => {
          const current = acting === location;
          return (
            <MenuItem
              key={_id}
              selected={current}
              onClick={() => (current ? setAnchor(null) : switchToBranch(location))}
              sx={{ fontSize: 13, textTransform: "capitalize" }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {current && <CheckRoundedIcon sx={{ fontSize: 18 }} />}
              </ListItemIcon>
              {location}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
